import { useTranslation } from 'react-i18next';
import { Package, ClipboardList, Layers, AlertTriangle, BarChart3, TrendingDown, TrendingUp, FileWarning, Printer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SummaryStats } from '../types';
import { cn } from '@/lib/utils';
import { METRIC_COLORS, type MetricKey } from '../metricColors';
import { calcPercentDelta } from '../utils/delta';
import { CapacityCard } from './CapacityCard';

interface SummaryStatsCardsProps {
  stats: SummaryStats;
  trend: {
    direction: 'improving' | 'worsening' | 'stable';
    percent: number;
  };
  isLoading?: boolean;
  comparisonStats?: SummaryStats;
  /** Total prints in the selected date range (from history data) */
  totalPrints?: number;
  /** Comparison total prints for previous period */
  comparisonTotalPrints?: number;
  /**
   * Short human label for the selected date range (e.g. "Last 7 days",
   * "Jan 25 – Apr 24"). Rendered as a subtitle on range-sum cards so the
   * reader never has to look at the picker to know what the sum covers.
   */
  dateRangeLabel?: string;
  /**
   * Matching label for the comparison period. Shown under the delta so
   * the user can see which days are being compared against.
   */
  comparisonRangeLabel?: string;
  /**
   * Demand-vs-output figures for the capacity card, rendered in the grid's
   * spare cell. When omitted (e.g. the TV view) no capacity card is shown.
   */
  capacity?: {
    ordersPlaced: number;
    ordersFulfilled: number;
    unitsOrdered: number;
    unitsPrinted: number;
    isLoading?: boolean;
  };
}

interface MetricCardProps {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Drives icon colour + chip + left-edge accent for consistency with chart */
  metricKey: MetricKey;
  delta?: { value: number; inverted?: boolean } | null;
  subtitle?: string;
  comparisonRangeLabel?: string;
  isLoading?: boolean;
}

function MetricCard({ label, value, icon: Icon, metricKey, delta, subtitle, comparisonRangeLabel, isLoading }: MetricCardProps) {
  const color = METRIC_COLORS[metricKey];
  return (
    <Card
      className={cn(
        // Left-edge accent bar (2px) in the metric's colour — a restrained
        // visual hook that links each card to its chart series at a glance.
        'relative overflow-hidden transition-shadow hover:shadow-sm',
        "before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']",
        color.ring,
      )}
    >
      <CardContent className="pt-5 pb-4 pl-6 pr-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className="text-sm font-medium text-muted-foreground leading-tight">
            {label}
          </span>
          <span
            className={cn(
              'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
              color.bg,
            )}
            aria-hidden="true"
          >
            <Icon className={cn('h-4 w-4', color.text)} />
          </span>
        </div>
        <div className="text-2xl font-semibold tabular-nums tracking-tight">
          {isLoading ? <Skeleton className="h-8 w-20" /> : value}
        </div>
        {!isLoading && delta != null && delta.value !== 0 && (
          <ComparisonDelta value={delta.value} inverted={delta.inverted} rangeLabel={comparisonRangeLabel} />
        )}
        {!isLoading && subtitle && (
          <p className="text-[11px] text-muted-foreground mt-1.5 leading-tight">
            {subtitle}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ComparisonDelta({ value, inverted, rangeLabel }: { value: number; inverted?: boolean; rangeLabel?: string }) {
  const { t } = useTranslation('production');
  // For defect-related metrics, a decrease is good (inverted = true)
  const isPositiveChange = value > 0;
  const isGood = inverted ? !isPositiveChange : isPositiveChange;

  return (
    <div className="mt-1.5 space-y-0.5">
      <div
        className={cn(
          'inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
          isGood
            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
            : 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
        )}
      >
        {value > 0 ? (
          <TrendingUp className="h-3 w-3 mr-0.5" />
        ) : (
          <TrendingDown className="h-3 w-3 mr-0.5" />
        )}
        {value > 0 ? '+' : ''}{value.toFixed(1)}% {t('qa.stats.vsPrevPeriod')}
      </div>
      {rangeLabel && (
        <p className="text-[10px] text-muted-foreground leading-tight">{rangeLabel}</p>
      )}
    </div>
  );
}

export function SummaryStatsCards({
  stats,
  trend,
  isLoading,
  comparisonStats,
  totalPrints,
  comparisonTotalPrints,
  dateRangeLabel,
  comparisonRangeLabel,
  capacity,
}: SummaryStatsCardsProps) {
  const { t } = useTranslation('production');
  const totalOrders = stats.totalOrders ?? 0;
  const compTotalOrders = comparisonStats?.totalOrders ?? 0;
  const printFileIssues = stats.printFileIssues ?? 0;
  const compPrintFileIssues = comparisonStats?.printFileIssues ?? 0;

  const defectRate = stats.totalProcessed > 0
    ? (stats.totalDefects / stats.totalProcessed) * 100
    : 0;

  const avgUnitsPerOrder = totalOrders > 0
    ? stats.totalProcessed / totalOrders
    : 0;

  // Comparison calculations
  const compDefectRate = comparisonStats && comparisonStats.totalProcessed > 0
    ? (comparisonStats.totalDefects / comparisonStats.totalProcessed) * 100
    : 0;

  const compAvgUnitsPerOrder = compTotalOrders > 0
    ? (comparisonStats?.totalProcessed ?? 0) / compTotalOrders
    : 0;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      <MetricCard
        label={t('qa.stats.totalUnits')}
        value={stats.totalProcessed.toLocaleString()}
        icon={Package}
        metricKey="itemsProcessed"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(stats.totalProcessed, comparisonStats.totalProcessed) } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      <MetricCard
        label={t('qa.stats.totalOrders')}
        value={totalOrders.toLocaleString()}
        icon={ClipboardList}
        metricKey="totalOrders"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(totalOrders, compTotalOrders) } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      <MetricCard
        label={t('qa.stats.totalPrints')}
        value={(totalPrints ?? 0).toLocaleString()}
        icon={Printer}
        metricKey="itemsPrinted"
        isLoading={isLoading}
        delta={comparisonStats && comparisonTotalPrints != null ? { value: calcPercentDelta(totalPrints ?? 0, comparisonTotalPrints) } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      <MetricCard
        label={t('qa.stats.avgUnitsPerOrder')}
        value={avgUnitsPerOrder.toFixed(1)}
        icon={Layers}
        metricKey="avgUnitsPerOrder"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(avgUnitsPerOrder, compAvgUnitsPerOrder) } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      <MetricCard
        label={t('qa.stats.totalDefects')}
        value={stats.totalDefects.toLocaleString()}
        icon={AlertTriangle}
        metricKey="defects"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(stats.totalDefects, comparisonStats.totalDefects), inverted: true } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      <MetricCard
        label={t('qa.stats.defectRate')}
        value={`${defectRate.toFixed(1)}%`}
        icon={BarChart3}
        metricKey="defectRate"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(defectRate, compDefectRate), inverted: true } : null}
        comparisonRangeLabel={comparisonRangeLabel}
        subtitle={
          trend.direction === 'improving'
            ? t('qa.stats.qualityImproving')
            : trend.direction === 'worsening'
            ? t('qa.stats.needsAttention')
            : t('qa.stats.consistentQuality')
        }
      />
      <MetricCard
        label={t('qa.stats.printIssues')}
        value={printFileIssues.toLocaleString()}
        icon={FileWarning}
        metricKey="printIssues"
        isLoading={isLoading}
        delta={comparisonStats ? { value: calcPercentDelta(printFileIssues, compPrintFileIssues), inverted: true } : null}
        subtitle={dateRangeLabel}
        comparisonRangeLabel={comparisonRangeLabel}
      />
      {capacity && (
        <CapacityCard
          ordersPlaced={capacity.ordersPlaced}
          ordersFulfilled={capacity.ordersFulfilled}
          unitsOrdered={capacity.unitsOrdered}
          unitsPrinted={capacity.unitsPrinted}
          isLoading={capacity.isLoading}
          dateRangeLabel={dateRangeLabel}
        />
      )}
    </div>
  );
}
